import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, isRef as _isRef } from "vue"

const _hoisted_1 = { class: "px-14 max-md:px-6 py-5" }
const _hoisted_2 = { class: "hidden lg:flex items-center" }
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = { class: "the-header__links inline-flex" }
const _hoisted_5 = { class: "flex lg:hidden items-center" }
const _hoisted_6 = ["aria-expanded", "aria-label"]
const _hoisted_7 = {
  id: "mobile-menu",
  class: "flex flex-col h-full w-full bg-monster-500 dark:bg-monster-950 bg-repeat bg-[url('/image/textures/items.png')] bg-[length:450px]"
}
const _hoisted_8 = { class: "text-center grow flex items-center justify-center" }
const _hoisted_9 = { class: "pt-10" }
const _hoisted_10 = {
  class: "mobile-menu-nav flex flex-col justify-end",
  "aria-label": "Mobile navigation"
}
const _hoisted_11 = { class: "bg-monster-700 dark:bg-black/35 text-center py-3" }

const monster = '/image/monster.png'
const logoText = '/image/logo-primary.png'


export default /*@__PURE__*/_defineComponent({
  __name: 'TheHeader',
  props: {
    absolute: { type: Boolean, default: false }
  },
  setup(__props: any) {



const opened = ref(false)
// Keep slideover mounted after first open so reopens don't re-fetch the
// LazyUSlideover chunk and re-init Reka UI internals.
const hasEverOpened = ref(false)
watch(opened, (o) => {
  if (o)
    hasEverOpened.value = true
})
const y = ref(0)
const lastY = ref(0)

// Reka UI (used by UModal / USlideover) sets `data-scroll-locked` on <body>
// when an overlay is open. Freeze the headroom state during that window so
// the header doesn't pop in/out when a modal is opened mid-page.
const overlayOpen = ref(false)
let observer: MutationObserver | null = null
let scrollFrame: number | null = null

function readScrollY() {
  y.value = window.scrollY || document.documentElement.scrollTop || 0
}

function queueScrollUpdate() {
  if (scrollFrame !== null)
    return
  scrollFrame = requestAnimationFrame(() => {
    scrollFrame = null
    readScrollY()
  })
}

onMounted(() => {
  if (typeof document === 'undefined')
    return
  readScrollY()
  window.addEventListener('scroll', queueScrollUpdate, { passive: true })
  const sync = () => {
    overlayOpen.value = document.body.hasAttribute('data-scroll-locked')
      || opened.value
  }
  sync()
  observer = new MutationObserver(sync)
  observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked', 'style'] })
})
onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', queueScrollUpdate)
  if (scrollFrame !== null) {
    cancelAnimationFrame(scrollFrame)
    scrollFrame = null
  }
})
watch(opened, () => {
  overlayOpen.value = document.body.hasAttribute('data-scroll-locked') || opened.value
})

const isScrolled = computed(() => y.value > 50)
const scrollingUp = computed(() => y.value < lastY.value)
const isVisible = computed(() => {
  if (overlayOpen.value)
    return lastY.value < 50
  return y.value < 50 || scrollingUp.value
})

watch(y, (newY, oldY) => {
  if (overlayOpen.value)
    return
  lastY.value = oldY ?? 0
})

const topLinks: Record<string, string> = {
  'Games': '/games',
  'Blog': '/blog',
  'Jobs': '/jobs',
  'About': '/about',
  'Monster Fund': '/monsterfund',
  'Contact': '/contact',
}

const drawerItems: Record<string, string> = {
  Home: '/',
  ...topLinks,
}
const drawerEntries = Object.entries(drawerItems)

const route = useRoute()
const headerSocialVariant = computed(() => {
  if (isScrolled.value || route.name === 'games-cult-of-the-lamb')
    return 'white'
  return 'header'
})

function nudgeDown(target: string) {
  if (typeof window === 'undefined')
    return
  // Don't nudge when navigating within the same top-level section.
  const currentTop = route.path.split('/')[1] ?? ''
  const targetTop = target.split('/')[1] ?? ''
  if (currentTop && currentTop === targetTop)
    return
  if (window.scrollY < 50)
    window.scrollTo({ top: 150, behavior: 'smooth' })
}

function toggleOpen() {
  opened.value = true
}

function toggleClose() {
  opened.value = false
}

async function openBrandKit(e: MouseEvent) {
  e.preventDefault()
  opened.value = false
  await navigateTo('/brand-kit')
}

const { play } = useSound()
function navWobble() {
  play('wobble', { throttle: 120, gain: 0.8, rateJitter: 0.05 })
}
function navTick(target: string) {
  play('tick', { throttle: 80, rateJitter: 0.06 })
  nudgeDown(target)
}
function drawerTick() {
  play('tick', { throttle: 80, rateJitter: 0.06 })
  toggleClose()
}
function drawerPop() {
  play('pop', { gain: 0.6, rateJitter: 0.05 })
  toggleClose()
}
function onMenuOpen() {
  play('expand', { gain: 0.7 })
  toggleOpen()
}
function onMenuClose() {
  play('collapse', { gain: 0.7 })
  toggleClose()
}

return (_ctx: any,_cache: any) => {
  const _component_NuxtImg = _resolveComponent("NuxtImg")!
  const _component_NuxtLink = _resolveComponent("NuxtLink")!
  const _component_MMotionWobble = _resolveComponent("MMotionWobble")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_MDiscordButton = _resolveComponent("MDiscordButton")!
  const _component_MSocialLinks = _resolveComponent("MSocialLinks")!
  const _component_MMotionLift = _resolveComponent("MMotionLift")!
  const _component_LazyUSlideover = _resolveComponent("LazyUSlideover")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["the-header w-full leading-none z-[100]", [
      __props.absolute ? 'absolute top-0 left-0 text-white' : '',
      _unref(opened) ? 'the-header--mobile-nav' : '',
    ]])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["headroom fixed inset-x-0 top-0 z-[100] transition-[transform,background-color] duration-250", [
        _unref(isVisible) ? 'translate-y-0' : '-translate-y-full',
        _unref(isScrolled) ? 'headroom--not-top bg-monster-500 dark:bg-monster-950/92 dark:backdrop-blur-md' : '',
      ]])
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_NuxtLink, {
            class: "the-header__logo no-underline inline-flex mr-4 max-md:mr-2 shrink-0",
            to: "/",
            title: "Right-click for brand kit",
            onContextmenu: openBrandKit
          }, {
            default: _withCtx(() => [
              _createVNode(_component_NuxtImg, {
                src: logoText,
                alt: "Massive Monster",
                width: 155,
                height: 53,
                densities: "x1 x2"
              })
            ]),
            _: 1
          }),
          _cache[2] || (_cache[2] = _createElementVNode("div", { class: "grow" }, null, -1)),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              (_openBlock(), _createElementBlock(_Fragment, null, _renderList(topLinks, (item, key) => {
                return _createVNode(_component_NuxtLink, {
                  key: key,
                  class: "font-bold no-underline inline-flex py-3 px-6",
                  to: item,
                  onClick: ($event: any) => (navTick(item)),
                  onMouseenter: navWobble
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_MMotionWobble, { loud: "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(key), 1)
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 2
                }, 1032, ["to", "onClick"])
              }), 64)),
              _createElementVNode("a", {
                class: "font-bold no-underline inline-flex py-3 px-6",
                href: "https://merch.devolverdigital.co/collections/cult-of-the-lamb",
                target: "_blank",
                onMouseenter: navWobble
              }, [
                _createVNode(_component_MMotionWobble, { loud: "" }, {
                  default: _withCtx(() => [
                    _cache[1] || (_cache[1] = _createTextVNode(" Merch ", -1)),
                    _createVNode(_component_Icon, {
                      class: "ml-1 mb-1 pl-1 text-white text-[0.75em] h-[13.5px]",
                      name: "fa6-solid:up-right-from-square"
                    })
                  ]),
                  _: 1
                })
              ], 32)
            ]),
            _createVNode(_component_MDiscordButton, {
              variant: "header",
              class: "hidden xl:inline-flex mx-6 self-center"
            }),
            _createVNode(_component_MSocialLinks, {
              variant: _unref(headerSocialVariant),
              size: "lg",
              sound: ""
            }, null, 8, ["variant"])
          ])
        ]),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_NuxtLink, {
            class: "the-header__logo no-underline inline-flex mr-4 max-md:mr-2 shrink-0",
            to: "/",
            title: "Right-click for brand kit",
            onContextmenu: openBrandKit
          }, {
            default: _withCtx(() => [
              _createVNode(_component_NuxtImg, {
                src: logoText,
                alt: "Massive Monster Logo",
                width: 155,
                height: 53,
                densities: "x1 x2"
              })
            ]),
            _: 1
          }),
          _cache[3] || (_cache[3] = _createElementVNode("div", { class: "grow" }, null, -1)),
          _createElementVNode("button", {
            class: "mobile-menu-trigger btn-textured",
            type: "button",
            "aria-expanded": _unref(opened),
            "aria-label": _unref(opened) ? 'Close menu' : 'Open menu',
            onClick: onMenuOpen
          }, [
            _createVNode(_component_MMotionWobble, { loud: "" }, {
              default: _withCtx(() => [
                (!_unref(opened))
                  ? (_openBlock(), _createBlock(_component_Icon, {
                      key: 0,
                      name: "fa6-solid:bars-staggered",
                      class: "mobile-menu-trigger__icon"
                    }))
                  : (_openBlock(), _createBlock(_component_Icon, {
                      key: 1,
                      name: "fa6-solid:xmark",
                      class: "mobile-menu-trigger__icon"
                    }))
              ]),
              _: 1
            })
          ], 8, _hoisted_6)
        ])
      ])
    ], 2),
    (_unref(opened) || _unref(hasEverOpened))
      ? (_openBlock(), _createBlock(_component_LazyUSlideover, {
          key: 0,
          open: _unref(opened),
          "onUpdate:open": _cache[0] || (_cache[0] = ($event: any) => (_isRef(opened) ? (opened).value = $event : null)),
          side: "right",
          ui: { content: 'sm:max-w-full w-full bg-monster-600 dark:bg-monster-950 text-white' },
          close: false
        }, {
          content: _withCtx(() => [
            _createElementVNode("div", _hoisted_7, [
              _createElementVNode("button", {
                class: "absolute top-6 right-6 w-10 h-10 p-0 bg-transparent border-0 cursor-pointer text-white z-[100] [&_svg]:w-full [&_svg]:h-full",
                type: "button",
                "aria-label": "Close menu",
                onClick: onMenuClose
              }, [
                _createVNode(_component_MMotionWobble, { loud: "" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_Icon, { name: "fa6-solid:xmark" })
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_component_MMotionLift, {
                delay: 0.05,
                class: "absolute top-6 left-6 z-[100]"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_NuxtLink, {
                    class: "inline-flex p-0 bg-transparent border-0 cursor-pointer no-underline [&_img]:block [&_img]:h-9 [&_img]:w-auto [&_img]:brightness-0 [&_img]:invert",
                    to: "/",
                    "aria-label": "Massive Monster home",
                    onClick: drawerTick
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_NuxtImg, {
                        src: logoText,
                        alt: "Massive Monster",
                        width: 155,
                        height: 53,
                        densities: "x1 x2"
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("nav", _hoisted_10, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(drawerEntries), ([key, item], index) => {
                      return (_openBlock(), _createElementBlock("div", {
                        key: key,
                        class: "mobile-menu-nav__item",
                        style: _normalizeStyle({ '--menu-index': index })
                      }, [
                        _createVNode(_component_NuxtLink, {
                          class: "text-white no-underline uppercase p-3 text-xl font-bold inline-block",
                          to: item,
                          onClick: drawerTick,
                          onMouseenter: navWobble
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_MMotionWobble, { loud: "" }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(key), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1032, ["to"])
                      ], 4))
                    }), 128)),
                    _createElementVNode("div", {
                      class: "mobile-menu-nav__item",
                      style: _normalizeStyle({ '--menu-index': _unref(drawerEntries).length })
                    }, [
                      _createElementVNode("a", {
                        class: "text-white no-underline uppercase p-3 text-xl font-bold inline-block",
                        href: "https://merch.devolverdigital.co/collections/cult-of-the-lamb",
                        target: "_blank",
                        onMouseenter: navWobble
                      }, [
                        _createVNode(_component_MMotionWobble, { loud: "" }, {
                          default: _withCtx(() => [
                            _cache[4] || (_cache[4] = _createTextVNode(" Merch ", -1)),
                            _createVNode(_component_Icon, {
                              class: "ml-1 mb-1 pl-1 text-white text-[0.75em]",
                              name: "fa6-solid:up-right-from-square"
                            })
                          ]),
                          _: 1
                        })
                      ], 32)
                    ], 4)
                  ]),
                  _createVNode(_component_MMotionLift, {
                    delay: 0.4,
                    as: "div",
                    class: "inline-block mt-10"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_NuxtLink, {
                        to: "/",
                        "aria-label": "Massive Monster home",
                        class: "inline-block",
                        onClick: drawerPop
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_MMotionWobble, { loud: "" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_NuxtImg, {
                                src: monster,
                                loading: "lazy",
                                alt: "Monster",
                                height: 100
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ])
              ]),
              _createElementVNode("div", _hoisted_11, [
                _createVNode(_component_MSocialLinks, {
                  variant: _unref(headerSocialVariant) === 'white' ? 'white' : (__props.absolute ? 'primary' : 'footer'),
                  sound: ""
                }, null, 8, ["variant"])
              ])
            ])
          ]),
          _: 1
        }, 8, ["open"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})